import { usePrefetchQuery, useQuery } from '@tanstack/react-query';

import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { type ClientHeatmapParams } from '@/features/reports/api';
import { HEATMAP_COPY as COPY } from '@/features/reports/copy';
import { ActivityLogCard } from '@/features/reports/heatmap/ActivityLogCard';
import { DemandCard } from '@/features/reports/heatmap/DemandCard';
import { EngagementCard } from '@/features/reports/heatmap/EngagementCard';
import { PageAttentionCard } from '@/features/reports/heatmap/PageAttentionCard';
import { heatmapHint } from '@/features/reports/heatmap/labels';
import { ManagerFilter, ReportFailure } from '@/features/reports/parts';
import { clientHeatmapQuery, lookbookAttentionQuery } from '@/features/reports/queries';
import { HEATMAP_WINDOWS, type HeatmapWeeks } from '@/features/reports/tabs';

/**
 * Reports › Client heatmap (ALS-88): the app's activity log read three ways, client engagement,
 * brand and category demand and the log itself, for six, four or two weeks. One request for the
 * window and the manager, so the tab loads and fails as one; the manager is the page's, the same
 * choice as Clients' and App usage's, and it narrows engagement and the log but not demand.
 *
 * Lookbook page attention (ALS-89) is the row's third card, with a request of its own that takes
 * the window and the lookbook but no manager, since it is not per client. It starts with the
 * tab's, so the card does not wait for the report to arrive before asking.
 */
export function HeatmapPanel({
    params,
    lookbookId,
    onWeeksChange,
    onManagerChange,
    onLookbookChange,
}: {
    params: ClientHeatmapParams;
    lookbookId: number | undefined;
    onWeeksChange: (weeks: HeatmapWeeks) => void;
    onManagerChange: (rmId: number | undefined) => void;
    onLookbookChange: (lookbookId: number) => void;
}) {
    const report = useQuery(clientHeatmapQuery(params));
    usePrefetchQuery(lookbookAttentionQuery({ weeks: params.weeks, lookbookId }));
    const data = report.data;

    return (
        <div className="flex min-w-0 flex-col gap-(--layout-row-gap)">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <SegmentedControl
                        tone="solid"
                        aria-label={COPY.window}
                        value={String(params.weeks)}
                        onValueChange={(next) => onWeeksChange(Number(next) as HeatmapWeeks)}
                    >
                        {HEATMAP_WINDOWS.map((weeks) => (
                            <SegmentedControlItem
                                key={weeks}
                                value={String(weeks)}
                                className="pointer-coarse:min-w-11"
                            >
                                {COPY.weeks(weeks)}
                            </SegmentedControlItem>
                        ))}
                    </SegmentedControl>
                    <ManagerFilter rmId={params.rmId} onManagerChange={onManagerChange} />
                </div>
                {data ? (
                    <span className="text-muted-foreground text-xs">{heatmapHint(data)}</span>
                ) : null}
            </div>

            {report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid gap-(--layout-row-gap) xl:grid-cols-2">
                        <Skeleton className="h-[320px] rounded-md" />
                        <Skeleton className="h-[320px] rounded-md" />
                    </div>
                    <Skeleton className="h-[240px] rounded-md" />
                </div>
            ) : report.isError && !data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void report.refetch()} />
            ) : data ? (
                <>
                    {/* Two cards side by side on a desk with page attention under them, all
                        three in a row on a wide screen, as the prototype's .rp-row--hm lays them
                        out, and stacked below a desk: each grid scrolls sideways inside its own
                        card, so none forces the page wider. The desk rules stop at 1850px:
                        Tailwind emits an arbitrary min-[1850px] before the named xl, so an
                        open-ended xl would win on a wide screen too (ALS-483). Page attention's
                        wrapper is a grid of its own so the card fills the row's height, as the
                        other two do, rather than ending at its content (ALS-514). */}
                    <div className="grid min-w-0 gap-(--layout-row-gap) min-[1850px]:grid-cols-[5fr_4fr_5fr] xl:max-[1850px]:grid-cols-[3fr_2fr]">
                        <EngagementCard report={data} />
                        <DemandCard report={data} />
                        <div className="grid min-w-0 xl:max-[1850px]:col-span-full">
                            <PageAttentionCard
                                weeks={params.weeks}
                                lookbookId={lookbookId}
                                onLookbookChange={onLookbookChange}
                            />
                        </div>
                    </div>
                    <ActivityLogCard report={data} params={params} />
                </>
            ) : null}
        </div>
    );
}
